import CustomInput from "components/CustomInput";
import MonthYearPicker from "components/MonthYearPicker/MonthYearPicker";
import { licensePlateTypesList } from "features/vehicles/vehicleConsts";
import moment from "moment";
import VehicleService from "services/vehicleService";
import { arrayToObject } from "utils/object-util";

export const activityStatuses = {
    RequestedByClient: 1,
    RequestedByVendor: 2,
    InProgress: 3,
    Completed: 4,
    PendingApproval: 5,
    PendingInvoice: 6,
    Cancelled: 7,
    CompleteBilling: 8,
    Closed: 9,
    PendingClientApproval: 10,
    AutoRequested: 11,
    PricingApprovedByClient: 12,
    Exception: 13,
    PendingClientDecision: 14,
    CompletedAndPaid: 16,
    CompleteInspectionFailed: 17,
    CompleteInspectionPassed: 18,
    WaitingOnClientToSend: 19
}

export const activityStatusOrder = {
    [activityStatuses.AutoRequested]: 1,
    [activityStatuses.RequestedByClient]: 1,
    [activityStatuses.RequestedByVendor]: 1,
    [activityStatuses.PendingClientDecision]: 2,
    [activityStatuses.PendingApproval]: 3,
    [activityStatuses.PendingClientApproval]: 4,
    [activityStatuses.PricingApprovedByClient]: 5,
    [activityStatuses.InProgress]: 6,
    [activityStatuses.WaitingOnClientToSend]: 6,
    [activityStatuses.PendingInvoice]: 7,
    [activityStatuses.CompleteInspectionPassed]: 7,
    [activityStatuses.CompleteInspectionFailed]: 7,
    [activityStatuses.Completed]: 8,
    [activityStatuses.CompleteBilling]: 9,
    [activityStatuses.CompletedAndPaid]: 10,
    [activityStatuses.Exception]: 10,
    [activityStatuses.Closed]: 10,
    [activityStatuses.Cancelled]: 10
}

export const activityStatusesList = [
    { id: activityStatuses.RequestedByClient, name: 'Requested by Client', shortName: 'Requested' },
    { id: activityStatuses.RequestedByVendor, name: 'Recommended by Technician', shortName: 'Recommended' },
    { id: activityStatuses.InProgress, name: 'In Progress' },
    { id: activityStatuses.Completed, name: 'Completed', isClosed: true },
    { id: activityStatuses.PendingApproval, name: 'Pending Pricing Confirmation' },
    { id: activityStatuses.PendingInvoice, name: 'Complete & Pending Invoice' },
    { id: activityStatuses.Cancelled, name: 'Cancelled', isClosed: true },
    { id: activityStatuses.CompleteBilling, name: 'Complete Billing', isClosed: true },
    { id: activityStatuses.Closed, name: 'Closed', isClosed: true },
    { id: activityStatuses.PendingClientApproval, name: 'Pending Client Approval of Quote' },
    { id: activityStatuses.AutoRequested, name: 'Auto Requested (SLA)' },
    { id: activityStatuses.PricingApprovedByClient, name: 'Approved and Ready to Execute' },
    { id: activityStatuses.Exception, name: 'Exception' },
    { id: activityStatuses.PendingClientDecision, name: 'Pending Client Approval to Solicit Quote' },
    { id: activityStatuses.CompletedAndPaid, name: 'Completed & Paid' },
    { id: activityStatuses.CompleteInspectionFailed, name: 'Complete - Inspection Failed' },
    { id: activityStatuses.CompleteInspectionPassed, name: 'Complete - Inspection Passed' },
    { id: activityStatuses.WaitingOnClientToSend, name: 'Waiting on Client to Send' },
]

export const activityStatusById = arrayToObject(activityStatusesList);

export const completeStatuses = [
    activityStatuses.PendingInvoice,
    activityStatuses.Completed,
    activityStatuses.CompleteBilling,
    activityStatuses.Closed,
    activityStatuses.CompletedAndPaid,
    activityStatuses.Cancelled,
    activityStatuses.CompleteInspectionPassed,
    activityStatuses.CompleteInspectionFailed,
]

export const vendorStatusWindow = [
    activityStatuses.PricingApprovedByClient,
    activityStatuses.InProgress,
    activityStatuses.Completed,
]

export const isVendorEditLocked = (activity, isVendor) =>
    isVendor && completeStatuses.includes(activity?.activityId);

export const isVendorStatusLocked = (activity, isVendor) =>
    isVendor && (
        !vendorStatusWindow.includes(activity?.activityId) ||
        activity?.activityId === activityStatuses.Completed
    );

export const activityColors = {
    [activityStatuses.PendingApproval]: '#F6B6CD',
    [activityStatuses.PendingClientApproval]: '#FCC354',
    [activityStatuses.AutoRequested]: '#FC9154',
    [activityStatuses.RequestedByClient]: '#FC9154',
    [activityStatuses.RequestedByVendor]: '#FC9154',
    [activityStatuses.PricingApprovedByClient]: '#64E5C6',
    [activityStatuses.InProgress]: '#FFB240',
    [activityStatuses.PendingInvoice]: '#9bdc8a',
    [activityStatuses.Completed]: '#64E5C6',
    [activityStatuses.CompleteBilling]: '#9bdc8a',
    [activityStatuses.Closed]: '#9bdc8a',
    [activityStatuses.Cancelled]: '#e85b79',
    [activityStatuses.Exception]: '#7C6FF9',
    [activityStatuses.CompletedAndPaid]: '#64E5C6',
    [activityStatuses.CompleteInspectionPassed]: '#9bdc8a',
    [activityStatuses.CompleteInspectionFailed]: '#e85b79',
    [activityStatuses.WaitingOnClientToSend]: '#FCC354',
}

export const activityCustomFieldMappingEnum = {
    LicensePlateNumber: 1,
    RegistrationExpiration: 2,
    LicensePlateState: 3,
    LicensePlateType: 4,
}

export const activityCustomFieldSetup = {
    [activityCustomFieldMappingEnum.LicensePlateNumber]: {
        mappedValue: (vehicle) => vehicle?.licensePlate
    },
    [activityCustomFieldMappingEnum.LicensePlateState]: {
        component: (customField, value, onChange) => <CustomInput
            id="ddlCustomFieldData"
            label={customField?.label}
            elementType="dropdown"
            value={value}
            onChange={(val) => onChange(customField, val)}
            values={[{ name: '', id: null }, ...VehicleService.getUsStates()]}
            touched={true}
        />,
        mappedValue: (vehicle) => vehicle?.licensePlateState
    },
    [activityCustomFieldMappingEnum.RegistrationExpiration]: {
        component: (customField, value, onChange) => <MonthYearPicker
            label={customField?.label}
            onChange={(value) => {
                const val = moment(value).format("YYYY-MM");
                onChange(customField, val);
            }}
            value={value} />,
        mappedValue: (vehicle) => vehicle?.registrationYear && vehicle?.registrationMonth ? moment(new Date(vehicle?.registrationYear, vehicle?.registrationMonth)).format("YYYY-MM") : ""
    },
    [activityCustomFieldMappingEnum.LicensePlateType]: {
        component: (customField, value, onChange) => <CustomInput
            id="licensePlateType"
            label={customField?.label}
            value={parseInt(value)}
            elementType="dropdown"
            onChange={(val) => onChange(customField, val)}
            values={licensePlateTypesList || []}
            showEmpty
        />,
        mappedValue: (vehicle) => vehicle?.licensePlateType?.toString()
    }
}

export const activityItemTypeTags = {
    BasicWash: 1,
    PreTRInspection: 2,
    PostTRInspection: 3,
    LicensePlateOrdered: 4,
    AcquiringTempTag: 5,
    Exercised: 6,
    PaveInspection: 7,
    LicensePlateArrived: 8,
    LicensePlateInstalled: 9,
    DOTInspection: 10,
    Inspection: 11,
}

export const activityStatusReasons = {
    InclementWeather: 1,
    OffLotMostOfCycle: 2,
    WasBeingWorkedOn: 3,
    Other: 4
}

export const activityDashboardActivitiesArgs = { includeCleaning: true };